import { useEffect, useState } from 'react';
import {
  LocalizationProvider,
  MobileDatePicker,
  MobileTimePicker,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TextField } from '@mui/material';
import { useTaskContext } from '../../../../context/TaskContext';
import { useSelector } from 'react-redux';
import { parseISO } from 'date-fns';

const normalizeDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string') {
    const parsedIso = parseISO(value);
    if (!Number.isNaN(parsedIso.getTime())) return parsedIso;

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const isSameMoment = (left, right) => {
  const leftTime = left instanceof Date ? left.getTime() : null;
  const rightTime = right instanceof Date ? right.getTime() : null;
  return leftTime === rightTime;
};

const StartEndDateTimeTaskNew = () => {
  const { OnUpdateTask, isObserver } = useTaskContext()
  const { active_task } = useSelector(state => state.task)
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [errorStartDate] = useState(false);
  const [errorEndDate] = useState(false);
  const [dueTime, setDueTime] = useState(null);
  const [errorDueTime] = useState(false);
  const handleTimeChange = (time) => {
    if (isObserver()) return;
    const normalizedTime = normalizeDate(time);
    if (!isSameMoment(normalizedTime, dueTime)) {
      setDueTime(normalizedTime);
    }
    if (!isSameMoment(normalizedTime, normalizeDate(active_task?.due_time))) {
      OnUpdateTask({ due_time: normalizedTime });
    }
  };

  const handleStartDateChange = (date) => {
    if (isObserver()) return;
    const normalizedDate = normalizeDate(date);
    if (!isSameMoment(normalizedDate, startDate)) {
      setStartDate(normalizedDate);
    }
    if (!isSameMoment(normalizedDate, normalizeDate(active_task?.start_date))) {
      OnUpdateTask({ start_date: normalizedDate?.toISOString() || null, save_type: 'startdate' });
    }
  };

  const handleEndDateChange = (date) => {
    if (isObserver()) return;
    const normalizedDate = normalizeDate(date);
    if (!isSameMoment(normalizedDate, endDate)) {
      setEndDate(normalizedDate);
    }
    if (!isSameMoment(normalizedDate, normalizeDate(active_task?.end_date))) {
      OnUpdateTask({ end_date: normalizedDate?.toISOString() || null, save_type: 'duedate' });
    }
  };

  useEffect(() => {
    if (!active_task) return;

    const nextStartDate = normalizeDate(active_task?.start_date);
    if (!isSameMoment(nextStartDate, startDate)) {
      setStartDate(nextStartDate);
    }

    const nextEndDate = normalizeDate(active_task?.end_date);
    if (!isSameMoment(nextEndDate, endDate)) {
      setEndDate(nextEndDate);
    }

    const nextDueTime = normalizeDate(active_task?.due_time);
    if (!isSameMoment(nextDueTime, dueTime)) {
      setDueTime(nextDueTime);
    }
  }, [active_task, startDate, endDate, dueTime])

  return (
    <div className="flex gap-3 items-center flex-col">
      {/* ---------- Start Date ---------- */}
      <div className="w-full">
        <label className="text-grey-600 text-[12px]">Start date</label>

        <div className="relative w-full">
          <div
            style={{ backgroundSize: '20px' }}
            className="calenderIconsGray absolute left-1 top-2.5 z-10"
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MobileDatePicker
              className={`start_date_task_create_new !w-full ${errorStartDate ? 'bg-red-500' : 'bg-white'
                }`}
              closeOnSelect
              format="MMMM d, yyyy"
              value={startDate}
              maxDate={endDate}
              startDate={startDate}
              endDate={endDate}
              selected={startDate}
              selectsStart
              onChange={handleStartDateChange}
              slots={{ textField: TextField }}
              readOnly={isObserver()}
              slotProps={{
                textField: {
                  className: "newCssForTaskNewDesign",
                  placeholder: "Start date",
                  readOnly: true,
                  sx: {
                    '& .MuiInputBase-input::placeholder': {
                      color: 'rgb(107 114 128)',
                      opacity: 1,
                    },
                  }
                },
              }}
            />
          </LocalizationProvider>
        </div>
      </div>

      {/* ---------- End Date + Due Time ---------- */}
      <div className="flex items-center gap-3 justify-between w-full">
        {/* End Date */}
        <div className="w-full">
          <label className="text-grey-600 text-[12px]">End date</label>

          <div className="relative w-full">
            <div
              style={{ backgroundSize: '20px' }}
              className="calenderIconsGray absolute left-1 top-2 z-10"
            />

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <MobileDatePicker
                className={`start_date_task_create_new ${errorEndDate ? 'bg-red-500' : 'bg-white'
                  }`}
                closeOnSelect
                format="MMMM d, yyyy"
                value={endDate}
                minDate={startDate}
                selected={endDate}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                onChange={handleEndDateChange}
                slots={{ textField: TextField }}
                readOnly={isObserver()}
                slotProps={{
                  textField: {
                    placeholder: "End date",
                    className: "newCssForTaskNewDesign",
                    readOnly: true,
                    sx: {
                      '& .MuiInputBase-input::placeholder': {
                        color: 'rgb(107 114 128)',
                        opacity: 1,
                      },
                    }
                  },
                }}
              />
            </LocalizationProvider>
          </div>
        </div>

        {/* Due Time */}
        <div className="w-full">
          <label className="text-grey-600 text-[12px]">Due time</label>

          <div className="relative w-full">
            <div
              style={{ backgroundSize: '20px' }}
              className="clockIconsGray absolute left-1 top-2 z-10"
            />

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <MobileTimePicker
                className={`start_date_task_create_new ${errorDueTime ? 'bg-red-500' : 'bg-white'
                  }`}
                closeOnSelect
                value={dueTime}
                onChange={handleTimeChange}
                slots={{ textField: TextField }}
                readOnly={isObserver()}
                slotProps={{
                  textField: {
                    placeholder: "Due time",
                    className: "newCssForTaskNewDesign",
                    readOnly: true,
                    sx: {
                      '& .MuiInputBase-input::placeholder': {
                        color: 'rgb(107 114 128)',
                        opacity: 1,
                      },
                    }
                  },
                }}
              />
            </LocalizationProvider>
          </div>
        </div>
      </div>
    </div>
  );
};


export default StartEndDateTimeTaskNew;
